import { computed, inject, Injectable, signal, WritableSignal } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { Store } from '@ngrx/store';
import { DialogService } from 'primeng/dynamicdialog';
import { catchError, filter, Observable, of, switchMap, tap } from 'rxjs';
import { LundifySubscriptionsService } from 'src/app/core/services/lundify-subscriptions.service';
import { FirmSelectors } from 'src/app/core/state/firm/firm.selectors';
import { ClientType } from 'src/app/core/state/types';
import { LundifySubscriptionUser } from 'src/app/core/state/types/lundify-subscription-response.types';
import { openTranslatableDialog } from 'src/app/shared/misc/openDialog';
import { UpsertUserDialogComponent } from '../components/upsert-user-dialog.component';

@Injectable()
export class LundifyUsersFacade {
  private dialogService = inject(DialogService);
  private translationService = inject(TranslocoService);
  private store = inject(Store);
  private lundifySubscriptionsService: LundifySubscriptionsService = inject(LundifySubscriptionsService);
  private internalUsersData: WritableSignal<LundifySubscriptionUser[] | null> = signal(null);
  private error = signal(false);
  private loading = signal(false);

  adminUsers = computed(() => {
    const users = this.internalUsersData() ?? [];
    return users.filter((user) => user.role.toLowerCase() === 'admin');
  });

  collaboratorUsers = computed(() => {
    const users = this.internalUsersData() ?? [];
    return users.filter((user) => user.role.toLowerCase() === 'user');
  });

  hasError = computed(() => this.error());
  isLoading = computed(() => this.loading());

  loadSubscriptionUsers(clientId: number | string): Observable<LundifySubscriptionUser[]> {
    this.error.set(false);
    this.loading.set(true);

    return this.lundifySubscriptionsService.loadSubscriptionUsers(clientId).pipe(
      tap((data) => {
        this.internalUsersData.set(data);
        this.loading.set(false);
      }),
      catchError(() => {
        this.internalUsersData.set(null);
        this.error.set(true);
        this.loading.set(false);
        return of(null as unknown as LundifySubscriptionUser[]);
      }),
    );
  }

  openAddAdminDialog(client: ClientType, isAnnualSubscription: boolean): void {
    this.store
      .select(FirmSelectors.selectCountryCode)
      .pipe(
        switchMap((country) =>
          openTranslatableDialog({
            deps: { dialogService: this.dialogService, translationService: this.translationService, store: this.store },
            componentType: UpsertUserDialogComponent,
            data: { client, country, isAnnualSubscription },
          }),
        ),
        filter((result) => !!result?.user),
      )
      .subscribe((result) => {
        const currentUsers = this.internalUsersData() ?? [];
        if (!currentUsers.some((user) => user.email === result.user.email)) {
          this.internalUsersData.set([...currentUsers, result.user]);
        }
      });
  }
}
